import { Dispatch, SetStateAction, useMemo, useState } from 'react'

import { axiosInstance } from '../helpers'
import {
    EarlyAccessRequest,
    EarlyAccessRequestStatus,
    IdentifiableObject,
} from '../models'

export const useEarlyAccessRequest = () => {
    const [earlyAccessRequestList, setEarlyAccessRequestList] = useState<
        EarlyAccessRequest[]
    >([])
    const [
        pendingEarlyAccessCount,
        setPendingEarlyAccessCount,
    ] = useState<number>(0)
    const [
        pendingEarlyAccessRequestMap,
        setPendingEarlyAccessRequestMap,
    ] = useState<{ [key: number]: number }>({})
    const [
        earlyAccessRequestFilterState,
        setEarlyAccessRequestFilterState,
    ] = useState(initialEarlyAccessFilterState)
    const [
        earlyAccessLoadingState,
        setEarlyAccessLoadingState,
    ] = useState<EarlyAccessLoadingState>(initialEarlyAccessLoadingState)

    const getEarlyAccessRequestList = async () => {
        setEarlyAccessLoadingState({
            ...earlyAccessLoadingState,
            getEarlyAccessRequestList: true,
        })
        try {
            const res = await axiosInstance.get(
                `workorder/early-access-request/`,
                { params: { acknowledgeable: true } },
            )
            const newEarlyAccessRequestList: EarlyAccessRequest[] = res.data
            setEarlyAccessRequestList(newEarlyAccessRequestList)
            return newEarlyAccessRequestList
        } catch (e) {
            return Promise.reject(e)
        } finally {
            setEarlyAccessLoadingState({
                ...earlyAccessLoadingState,
                getEarlyAccessRequestList: false,
            })
        }
    }

    const updateEarlyAccessRequest = async (
        id: number,
        status: EarlyAccessRequestStatus,
    ) => {
        try {
            const request = {
                status: status,
            }
            const res = await axiosInstance.patch(
                `workorder/early-access-request/${id}/`,
                request,
            )
            const updatedEarlyAccessRequest = res.data
            _insertOrUpdateIdentifiable(
                updatedEarlyAccessRequest,
                setEarlyAccessRequestList,
            )
            setPendingEarlyAccessCount(pendingEarlyAccessCount - 1)
            return updatedEarlyAccessRequest
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const bulkUpdateEarlyAccessRequests = async (
        request: UpdateEarlyAccessRequestsRequest,
    ) => {
        setEarlyAccessLoadingState({
            ...earlyAccessLoadingState,
            updateEarlyAccessRequests: true,
        })
        try {
            const res = await axiosInstance.post(
                `workorder/early-access-request/bulk_update/`,
                request,
            )
            const updatedEarlyAccessRequests: EarlyAccessRequest[] = res.data
            updatedEarlyAccessRequests.forEach((updatedEarlyAccessRequest) => {
                _insertOrUpdateIdentifiable(
                    updatedEarlyAccessRequest,
                    setEarlyAccessRequestList,
                )
            })
            setPendingEarlyAccessCount(
                pendingEarlyAccessCount - updatedEarlyAccessRequests.length,
            )
            return updatedEarlyAccessRequests
        } catch (e) {
            return Promise.reject(e)
        } finally {
            setEarlyAccessLoadingState({
                ...earlyAccessLoadingState,
                updateEarlyAccessRequests: false,
            })
        }
    }

    const getPendingEarlyAccessCount = async () => {
        try {
            const res = await axiosInstance.get(
                `workorder/early-access-request/get_pending_request_count/`,
            )
            const pendingCount = res.data

            setPendingEarlyAccessCount(pendingCount)

            return pendingCount
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const filteredEarlyAccessRequests = useMemo(() => {
        const filters = earlyAccessRequestFilterState
        const filterEarlyAccessRequests = earlyAccessRequestList.filter(
            (earlyAccessRequest) => {
                const vendorValid =
                    !filters.vendorId ||
                    filters.vendorId === -1 ||
                    earlyAccessRequest.company_id === filters.vendorId
                const serviceValid =
                    !filters.serviceId ||
                    filters.serviceId === -1 ||
                    earlyAccessRequest.workorder.service_id ===
                        filters.serviceId
                return vendorValid && serviceValid
            },
        )
        return filterEarlyAccessRequests
    }, [earlyAccessRequestList, earlyAccessRequestFilterState])

    const updateEarlyAccessRequestState = (
        earlyAccessRequest: EarlyAccessRequest,
    ) => {
        _insertOrUpdateIdentifiable(
            earlyAccessRequest,
            setEarlyAccessRequestList,
        )
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []
            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }
            if (!found) {
                safeOldValue.push(updatedValue)
            }
            return safeOldValue
        })
    }

    return {
        earlyAccessRequestList,
        pendingEarlyAccessCount,
        filteredEarlyAccessRequests,
        earlyAccessRequestFilterState,
        earlyAccessLoadingState,
        pendingEarlyAccessRequestMap,
        getEarlyAccessRequestList,
        getPendingEarlyAccessCount,
        updateEarlyAccessRequest,
        setEarlyAccessRequestFilterState,
        updateEarlyAccessRequestState,
        bulkUpdateEarlyAccessRequests,
        setEarlyAccessLoadingState,
        setPendingEarlyAccessCount,
        setPendingEarlyAccessRequestMap,
    }
}

export interface EarlyAccessRequestFilterState {
    vendorId: number | null
    serviceId: number | null
}

const initialEarlyAccessFilterState: EarlyAccessRequestFilterState = {
    vendorId: null,
    serviceId: null,
}

export interface UpdateEarlyAccessRequestsRequest {
    early_access_request_ids: number[]
    status: EarlyAccessRequestStatus
}

export interface EarlyAccessLoadingState {
    getEarlyAccessRequestList: boolean
    updateEarlyAccessRequests: boolean
}

const initialEarlyAccessLoadingState: EarlyAccessLoadingState = {
    getEarlyAccessRequestList: false,
    updateEarlyAccessRequests: false,
}
